/**
 * NotesPanel - shared component for displaying and adding notes on any record.
 *
 * Reusable across: registration detail, claim detail, customer detail, dealer detail.
 * Clara Vale design system throughout.
 *
 * Notes display newest-first. Each note shows: title, body, category badge,
 * visibility badge, author, timestamp. Add note form collapses by default.
 */

import { useState, useEffect, useCallback, useId, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageSquare, Loader2, Plus, Lock, Eye, Paperclip, FileText, Milestone } from 'lucide-react'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { CardDragHandle } from '@/shared/components/card-drag-handle'
import { z } from 'zod'
import api from '@/admin/lib/api'
import { useUploadFile, type UploadedFile } from '@/admin/hooks/use-products'
import { FileUploader } from '@/shared/components/ui/file-uploader'
import { humanizeAccept, type UploadedFileItem } from '@/shared/lib/file-types'

const NOTE_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,video/mp4,video/quicktime'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import {
  MentionTextarea,
  MentionedText,
  type MentionUser,
} from '@/shared/components/ui/mention-textarea'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NoteRecordType = 'registration' | 'claim' | 'transfer' | 'inspection'
type NoteVisibility = 'internal' | 'customer_visible'

type NoteCategory =
  | 'general'
  | 'technical'
  | 'billing'
  | 'escalation'
  | 'warranty'
  // Written by the letter send only; never offered when adding a note.
  | 'letter'

interface NoteData {
  id: number
  uuid: string
  title: string
  content: string
  category: NoteCategory
  is_internal: boolean
  // The record's stage when the note was written, in the record's own words.
  // Blank on notes written before it was recorded.
  stage_label?: string
  created_by_name: string
  created_at: string
  updated_at: string
  attachments?: UploadedFile[]
  mentions?: { uuid: string; name: string }[]
}

export interface NotesPanelProps {
  recordId: string
  recordType: NoteRecordType
  canAddInternal: boolean
  canAddCustomerVisible: boolean
  allowAttachments?: boolean
  // The record's stage as its own page shows it, for a record whose stage the
  // server cannot work out itself (a registration's status is derived on the
  // page). Saved on a new note only when the server has no stage for it.
  stageLabel?: string
  className?: string
  onNoteAdded?: () => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORIES: { value: NoteCategory; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'technical', label: 'Technical' },
  { value: 'billing', label: 'Billing' },
  { value: 'escalation', label: 'Escalation' },
  { value: 'warranty', label: 'Warranty' },
]

const CATEGORY_STYLES: Record<NoteCategory, string> = {
  general: 'border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]',
  technical: 'border-[var(--rule-strong)] bg-transparent text-[var(--ink-2)]',
  billing: 'border-[var(--rule-strong)] bg-transparent text-[var(--ink-2)]',
  escalation: 'border-[var(--rule-strong)] bg-transparent text-[var(--ink-2)]',
  warranty: 'border-[var(--green-border)] bg-transparent text-[var(--green)]',
  letter: 'border-[var(--ink-3)] bg-transparent text-[var(--ink)]',
}

const MAX_CONTENT_LENGTH = 500

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  const day = d.getDate()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const mins = String(d.getMinutes()).padStart(2, '0')
  return `${day} ${month} ${year} · ${hours}:${mins}`
}

/**
 * Whether a note has genuinely been edited since it was written.
 *
 * ``created_at`` (auto_now_add) and ``updated_at`` (auto_now) are computed from
 * separate clock reads on the same INSERT, so they differ by microseconds on
 * every note ever saved - comparing them for equality marked all of them
 * "edited". A note is only edited if the two timestamps are a meaningful
 * distance apart.
 */
function wasEdited(note: Pick<NoteData, 'created_at' | 'updated_at'> | null): boolean {
  if (!note) return false
  const created = Date.parse(note.created_at)
  const updated = Date.parse(note.updated_at)
  if (Number.isNaN(created) || Number.isNaN(updated)) return false
  return updated - created > 1000
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function endpointForType(_type: NoteRecordType): string {
  return '/notes'
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotesPanel({
  recordId,
  recordType,
  canAddInternal,
  canAddCustomerVisible,
  allowAttachments = false,
  stageLabel,
  className,
  onNoteAdded,
}: NotesPanelProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // What "not internal" actually means on THIS surface.
  //
  // On a claim the audience really is the customer: the consumer portal renders
  // non-internal claim notes and the note is emailed to the claimant. On a
  // registration neither is true - every consumer-side note query is
  // claim-scoped, and the customer-visible dispatch is gated on the claim
  // record type - so the audience that genuinely gains sight of the note is the
  // external partner / contractor who can see the record. Calling it "Customer
  // visible" there would tell staff the homeowner had read something they never
  // receive, so each surface names its own audience.
  const externalAudienceLabel =
    recordType === 'claim'
      ? t('notes.visibility.customerVisible', 'Customer visible')
      : t('notes.visibility.partnerVisible', 'Shared with partner')
  // WPS-146 #9: the composer already read this from translation, but the
  // filter pill and the per-note badge spelled it out. A tenant relabelling
  // the pair (OC read it as Internal / External) got a dropdown that
  // disagreed with the badge on the note it had just written. One source.
  const internalAudienceLabel = t('notes.visibility.internal', 'Staff only')
  const [showForm, setShowForm] = useState(false)
  // WPS-166: the Overview lays its cards out in CSS masonry columns, so the
  // composer opening makes this card taller and the browser re-balances the
  // whole column flow beneath it. The page does not scroll, but every card
  // after this one moves - which reads as "Add Note threw me to the bottom of
  // the page". Bringing the composer into view (and putting the cursor in it)
  // turns that reflow into the thing the user asked for.
  const composerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!showForm) return
    // The re-balance lands a frame or two after the composer paints, so wait
    // until its position holds for a frame, then scroll ONCE. Scrolling on
    // every move re-fired all through the smooth scroll it had just started.
    // Bounded, so it can never spin.
    let raf = 0
    let tries = 0
    let lastTop: number | null = null
    const settle = () => {
      const el = composerRef.current
      const top = el ? Math.round(el.getBoundingClientRect().top) : null
      const settled = top !== null && top === lastTop
      lastTop = top
      if (!settled && tries++ < 30) {
        raf = requestAnimationFrame(settle)
        return
      }
      // Leave a composer that opened comfortably on screen where it is.
      if (el && top !== null && (top < 0 || top > window.innerHeight - 120)) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
    raf = requestAnimationFrame(settle)
    return () => cancelAnimationFrame(raf)
  }, [showForm])
  const [categoryFilter, setCategoryFilter] = useState<NoteCategory | 'all'>('all')
  // The ticket is titled "Internal and External notes views": staff need to read
  // one audience at a time, not scan a mixed list for the visibility badge.
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | NoteVisibility>('all')

  // Selected note for dialog
  const [selectedNote, setSelectedNote] = useState<NoteData | null>(null)

  // Form state
  const [saving, setSaving] = useState(false)
  const [attachments, setAttachments] = useState<UploadedFileItem[]>([])
  const [mentions, setMentions] = useState<MentionUser[]>([])
  const uploadAttachment = useUploadFile()
  // A claim detail page mounts several notes panels (overview, tabs, drawer),
  // so the textarea's id has to be unique per instance or the duplicate ids
  // break label/description association and any id-based lookup.
  const contentFieldId = `note-content-${useId()}`

  // Colleagues who can be @mentioned. Only fetched once the form is open and
  // only when staff-only notes are on offer, since a customer-visible note can
  // never carry a mention.
  const { data: mentionCandidates = [] } = useQuery<MentionUser[]>({
    queryKey: ['note-mentionable-users', recordType, recordId],
    // The api client's response interceptor already unwraps the
    // {status, message, result} envelope, so the payload IS r.data.
    // Record context only RANKS the list (people already on this record first);
    // it never changes who is mentionable.
    queryFn: () =>
      api
        .get('/notes/mentionable-users/', {
          params: { record_type: recordType, entity_id: recordId },
        })
        .then((r) => r.data ?? []),
    enabled: showForm && canAddInternal,
    staleTime: 5 * 60 * 1000,
  })

  const canAdd = canAddInternal || canAddCustomerVisible

  // Fetch notes. A query, so a note filed elsewhere (a sent letter) can
  // refresh this list by invalidating ['record-notes', recordType, recordId].
  const notesQuery = useQuery<NoteData[]>({
    queryKey: ['record-notes', recordType, recordId],
    queryFn: async () => {
      // As before: a failed load shows an empty list rather than retrying.
      try {
        const res = await api.get(`${endpointForType(recordType)}/`, {
          params: { entity_id: recordId, record_type: recordType, page_size: 200 },
        })
        const results: NoteData[] = res.data?.results ?? res.data ?? []
        return [...results].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
      } catch {
        return []
      }
    },
  })
  const notes = useMemo(() => notesQuery.data ?? [], [notesQuery.data])
  const loading = notesQuery.isPending
  const { refetch: refetchNotes } = notesQuery
  const fetchNotes = useCallback(async () => {
    await refetchNotes()
  }, [refetchNotes])

  const uploadAttachmentFile = useCallback(
    async (file: File): Promise<UploadedFileItem> => {
      try {
        const r = await uploadAttachment.mutateAsync({ file, use_case: 'CLAIM_EVIDENCE' })
        return {
          uuid: r.uuid,
          file_name: r.file_name,
          file_url: r.file_url,
          mime_type: (r as { mime_type?: string }).mime_type,
        }
      } catch (err: unknown) {
        toastApiError(err, 'Failed to upload attachment')
        throw err
      }
    },
    [uploadAttachment],
  )

  const noteFormFields = useMemo<FormField[]>(() => {
    const visibilityOptions = [
      ...(canAddInternal
        ? [{ value: 'internal', label: internalAudienceLabel }]
        : []),
      ...(canAddCustomerVisible
        ? [
            {
              value: 'customer_visible',
              label: externalAudienceLabel,
            },
          ]
        : []),
    ]
    return [
      {
        type: 'text',
        name: 'title',
        placeholder: 'Title (optional)',
        value: '',
      },
      {
        // Custom because an @mention picker over a textarea is a composite
        // control, not a styled textarea - the escape hatch this is for. The
        // validation below is the same schema the generated textarea carried.
        type: 'custom',
        name: 'content',
        value: '',
        validationSchema: z.string()
          .trim()
          .min(10, 'Notes must be at least 10 characters')
          .max(MAX_CONTENT_LENGTH, `Notes must be ${MAX_CONTENT_LENGTH} characters or fewer`)
          .min(1, 'Notes must be at least 10 characters'),
        render: (ctx) => {
          // Mentions are staff-only: offering the picker on a customer-visible
          // note would publish a colleague's name to the portal.
          const staffOnly = ctx.values.visibility === 'internal'
          const error = (ctx.errors.content as { message?: string } | undefined)?.message
          return (
            <div>
              <MentionTextarea
                id={contentFieldId}
                value={(ctx.values.content as string) ?? ''}
                onChange={(next) => ctx.setFieldValue('content', next)}
                candidates={staffOnly ? mentionCandidates : []}
                mentions={staffOnly ? mentions : []}
                onMentionsChange={setMentions}
                placeholder={
                  staffOnly
                    ? t(
                        'notes.placeholderWithMention',
                        'Write a note... type @ to mention a colleague',
                      )
                    : t('notes.placeholder', 'Write a note...')
                }
                maxLength={MAX_CONTENT_LENGTH}
              />
              {error && (
                <p className="mt-1.5 text-[11px] text-[var(--destructive)]">{error}</p>
              )}
            </div>
          )
        },
      },
      {
        type: 'select',
        name: 'category',
        label: t('notes.field.category', 'Category'),
        value: 'general',
        options: CATEGORIES.map((category) => ({
          value: category.value,
          // The label carried on the constant is the English default.
          label: t(`notes.category.${category.value}`, category.label),
        })),
      },
      {
        type: 'select',
        name: 'visibility',
        label: 'Visibility',
        value: canAddInternal ? 'internal' : 'customer_visible',
        options: visibilityOptions,
      },
    ]
  }, [
    canAddCustomerVisible,
    canAddInternal,
    contentFieldId,
    externalAudienceLabel,
    internalAudienceLabel,
    mentionCandidates,
    mentions,
    t,
  ])
  const { Form: NoteForm, ref: noteFormRef } = useForm(
    {
      name: `notes-panel-form-${recordType}-${recordId}`,
      submit: 'footer',
      fields: noteFormFields,
    },
    async (rawValues) => {
      const values = rawValues as {
        title: string
        content: string
        category: NoteCategory
        visibility: NoteVisibility
      }
      setSaving(true)
      try {
        await api.post(`${endpointForType(recordType)}/`, {
          entity_id: recordId,
          record_type: recordType,
          title: values.title.trim() || null,
          content: values.content.trim(),
          category: values.category,
          is_internal: values.visibility === 'internal',
          attachment_uuids: attachments.map((attachment) => attachment.uuid),
          mention_user_uuids:
            values.visibility === 'internal' ? mentions.map((user) => user.uuid) : [],
          ...(stageLabel ? { stage_label: stageLabel } : {}),
        })
        toast.success('Note saved')
        noteFormRef.current?.resetForm()
        setAttachments([])
        setMentions([])
        setShowForm(false)
        await fetchNotes()
        // The note's files are also filed on the record as Other (WPS-153), so
        // a Documents / Evidence card listing the record's attachments shows
        // them without a reload.
        if (attachments.length > 0) {
          queryClient.invalidateQueries({
            queryKey: ['subject-attachments', recordType, recordId],
          })
          // The claim Evidence tab reads the same list through its own key.
          queryClient.invalidateQueries({ queryKey: ['claim-attachments', recordId] })
        }
        onNoteAdded?.()
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        console.error('Note save failed:', status, err)
        toastApiError(err, `Failed to save note (${status ?? 'unknown'})`)
      } finally {
        setSaving(false)
      }
    },
    // `mentionCandidates` and `mentions` are read by the content field's
    // custom render closure, so they MUST be named here - the fields memo is
    // keyed on JSON, which drops the closure, and without them the picker
    // renders the empty candidate list it captured on first paint.
    [
      recordId,
      recordType,
      showForm,
      canAddInternal,
      canAddCustomerVisible,
      attachments,
      contentFieldId,
      mentionCandidates,
      mentions,
      queryClient,
      stageLabel,
    ],
  )

  // Filtered notes
  const matchesVisibility = (n: NoteData) =>
    visibilityFilter === 'all' ||
    (visibilityFilter === 'internal' ? n.is_internal : !n.is_internal)

  const filteredNotes = notes
    .filter((n) => categoryFilter === 'all' || n.category === categoryFilter)
    .filter(matchesVisibility)

  const internalCount = notes.filter((n) => n.is_internal).length
  const externalCount = notes.length - internalCount

  // Category counts for filter pills
  const categoryCounts = notes.reduce(
    (acc, n) => {
      acc[n.category] = (acc[n.category] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  // Loading state
  if (loading) {
    return (
      <div className={cn(className, 'p-6 text-center')}>
        <Loader2 className="mx-auto h-[18px] w-[18px] animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  // Empty state - matches DetailCard structure
  if (notes.length === 0 && !showForm) {
    return (
      <div
        className={cn(
          className,
          'overflow-hidden rounded-2xl border border-[var(--rule)] bg-[var(--bg)] shadow-[var(--shadow-card)]'
        )}
      >
        <div className="flex h-[60px] items-center justify-between border-b border-b-[var(--rule-light)] bg-[var(--panel)] px-5">
          <div className="flex items-center gap-3">
            <CardDragHandle />
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--green)_10%,transparent)] text-[var(--green)]"><MessageSquare className="h-4 w-4" /></span>
            <span className="text-[16px] font-medium text-[var(--ink)]">
              {t('notes.title', 'Notes')}
            </span>
          </div>
          {canAdd && (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setShowForm(true)}
              className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[14px] font-medium text-[var(--green)] transition-opacity duration-150 hover:opacity-70"
            >
              <Plus className="h-4 w-4" />
              {t('notes.addNote', 'Add note')}
            </Button>
          )}
        </div>
        <div className="bg-[var(--bg)] px-4 py-4">
          <p className="text-[13px] text-[var(--ink-3)]">
            {t('notes.noNotesYet', 'No notes yet')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        className,
        'overflow-hidden rounded-2xl border border-[var(--rule)] bg-[var(--bg)] shadow-[var(--shadow-card)]'
      )}
    >
      {/* Header - matches DetailCard style: icon chip and sentence-case title */}
      <div className="flex h-[60px] items-center justify-between border-b border-b-[var(--rule-light)] bg-[var(--panel)] px-5">
        <div className="flex items-center gap-3">
          <CardDragHandle />
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--green)_10%,transparent)] text-[var(--green)]"><MessageSquare className="h-4 w-4" /></span>
          <span className="text-[16px] font-medium text-[var(--ink)]">
            {t('notes.title', 'Notes')}
          </span>
        </div>
        {canAdd && !showForm && (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setShowForm(true)}
            className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[14px] font-medium text-[var(--green)] transition-opacity duration-150 hover:opacity-70"
          >
            <Plus className="h-4 w-4" />
            {t('notes.addNote', 'Add note')}
          </Button>
        )}
      </div>
      {/* Content - white background like DetailCard */}
      <div className="bg-[var(--bg)] px-4 py-3">

      {/* Audience filter - the Internal / External views the ticket asks for */}
      {notes.length > 0 && (internalCount > 0 || externalCount > 0) && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          <FilterPill
            label={`All notes (${notes.length})`}
            active={visibilityFilter === 'all'}
            onClick={() => setVisibilityFilter('all')}
          />
          <FilterPill
            label={`${internalAudienceLabel} (${internalCount})`}
            active={visibilityFilter === 'internal'}
            onClick={() =>
              setVisibilityFilter(visibilityFilter === 'internal' ? 'all' : 'internal')
            }
          />
          <FilterPill
            label={`${externalAudienceLabel} (${externalCount})`}
            active={visibilityFilter === 'customer_visible'}
            onClick={() =>
              setVisibilityFilter(
                visibilityFilter === 'customer_visible' ? 'all' : 'customer_visible',
              )
            }
          />
        </div>
      )}

      {/* Category filter pills */}
      {notes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <FilterPill
            label={`All (${notes.length})`}
            active={categoryFilter === 'all'}
            onClick={() => setCategoryFilter('all')}
          />
          {Object.entries(categoryCounts).map(([cat, count]) => (
            <FilterPill
              key={cat}
              label={`${cat.charAt(0).toUpperCase() + cat.slice(1)} (${count})`}
              active={categoryFilter === cat}
              category={cat as NoteCategory}
              onClick={() =>
                setCategoryFilter(
                  categoryFilter === cat ? 'all' : (cat as NoteCategory),
                )
              }
            />
          ))}
        </div>
      )}

      {/* Notes list - clickable line items */}
      <div className="flex flex-col">
        {filteredNotes.map((note) => (
          <Button
            key={note.uuid ?? note.id}
            variant="ghost"
            type="button"
            onClick={() => setSelectedNote(note)}
            className="flex h-auto w-full cursor-pointer items-start justify-between gap-4 rounded-none border-b-[0.5px] border-b-[var(--rule-light)] bg-transparent px-0 py-[9px] text-left whitespace-normal transition-colors hover:bg-[var(--bg-subtle)]"
          >
            {/* Left: who + title, then what kind of note and when in the
                record's life it was written. The second group wraps under the
                title when the card is narrow rather than clipping either. */}
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="flex min-w-0 max-w-full items-center gap-2">
                {/* The row identifies its author by initials to stay scannable at
                    this density. Two letters are not a name, so the full one is
                    carried for anyone who cannot infer it: `title` on hover, and
                    read out to a screen reader. It is spelled out in full when
                    the note is opened, and in the property notes summary. */}
                <span
                  title={note.created_by_name || 'System'}
                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[7px] font-normal uppercase tracking-[0.05em] text-[var(--green)]"
                >
                  <span aria-hidden="true">
                    {(note.created_by_name || '?')
                      .split(' ')
                      .map((w) => w[0])
                      .join('')
                      .slice(0, 2)}
                  </span>
                  <span className="sr-only">{note.created_by_name || 'System'}</span>
                </span>
                <span className="truncate text-[13px] text-[var(--ink)]">
                  {note.title || note.content.slice(0, 40) + (note.content.length > 40 ? '…' : '')}
                </span>
              </span>
              <span className="flex min-w-0 items-center gap-2">
                <CategoryBadge category={note.category} />
                <VisibilityIndicator
                  isInternal={note.is_internal}
                  internalLabel={internalAudienceLabel}
                  externalLabel={externalAudienceLabel}
                />
                {note.attachments && note.attachments.length > 0 && (
                  <span className="inline-flex shrink-0 items-center gap-[3px] text-[9px] font-normal tracking-[0.04em] text-[var(--ink-3)]">
                    <Paperclip className="h-[9px] w-[9px]" />
                    {note.attachments.length}
                  </span>
                )}
                <NoteStage stage={note.stage_label} />
              </span>
            </div>

            {/* Right: date */}
            <span className="shrink-0 whitespace-nowrap pt-px text-[11px] text-[var(--ink-3)]">
              {formatTimestamp(note.created_at)}
            </span>
          </Button>
        ))}
      </div>

      {/* Note detail dialog */}
      <Dialog open={!!selectedNote} onOpenChange={(open) => !open && setSelectedNote(null)}>
        <DialogContent className="max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
              {selectedNote?.title || 'Note'}
            </DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-1">
              <span
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[8px] font-normal uppercase tracking-[0.05em] text-[var(--green)]"
              >
                {(selectedNote?.created_by_name || '?')
                  .split(' ')
                  .map((w) => w[0])
                  .join('')
                  .slice(0, 2)}
              </span>
              <span className="whitespace-nowrap text-[11px] text-[var(--ink-3)]">
                {selectedNote?.created_by_name} · {selectedNote ? formatTimestamp(selectedNote.created_at) : ''}
                {wasEdited(selectedNote) && ' · edited'}
              </span>
              {selectedNote && <CategoryBadge category={selectedNote.category} />}
              {selectedNote && (
                <VisibilityIndicator
                  isInternal={selectedNote.is_internal}
                  internalLabel={internalAudienceLabel}
                  externalLabel={externalAudienceLabel}
                />
              )}
              <NoteStage stage={selectedNote?.stage_label} />
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 whitespace-pre-wrap text-[13px] leading-[1.7] text-[var(--ink)]">
            <MentionedText
              text={selectedNote?.content ?? ''}
              mentions={selectedNote?.mentions ?? []}
            />
          </div>
          {selectedNote?.attachments && selectedNote.attachments.length > 0 && (
            <div className="mt-4 rounded-[8px] border-[0.5px] border-[var(--rule)]">
              {selectedNote.attachments.map((attachment) => (
                <Button
                  key={attachment.uuid}
                  type="button"
                  variant="ghost"
                  onClick={() => attachment.file_url && window.open(attachment.file_url, '_blank')}
                  className="flex h-auto w-full items-center justify-between gap-3 rounded-none border-b-[0.5px] border-b-[var(--rule-light)] px-3 py-2.5 text-left last:border-b-0 hover:bg-[var(--bg-subtle)]"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                    <span className="truncate text-[12px] text-[var(--ink)]">
                      {attachment.file_name || attachment.uuid.slice(0, 8)}
                    </span>
                  </span>
                  <span className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[var(--green)]">
                    Open
                  </span>
                </Button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add note form */}
      {showForm && (
        <div
          ref={composerRef}
          className="panel-arrive mt-4"
        >
          <NoteForm className="flex flex-col gap-3" />
          <div className="mt-3">
            {allowAttachments && (
              <FileUploader
                value={attachments}
                onChange={setAttachments}
                uploadFn={uploadAttachmentFile}
                multiple
                accept={NOTE_ATTACHMENT_ACCEPT}
                acceptLabel={humanizeAccept(NOTE_ATTACHMENT_ACCEPT)}
              />
            )}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              type="submit"
              form={`notes-panel-form-${recordType}-${recordId}`}
              disabled={saving}
              className="inline-flex h-[34px] cursor-pointer items-center gap-1.5 rounded-[4px] bg-[var(--green)] px-4 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--bg)] transition-opacity duration-150 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saving && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              Save Note
            </Button>
            <Button
              type="button"
              variant="link"
              onClick={() => {
                noteFormRef.current?.resetForm()
                setAttachments([])
                setShowForm(false)
              }}
              className="cursor-pointer bg-transparent px-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] transition-colors duration-150 hover:text-[var(--ink)]"
            >
              Discard
            </Button>
          </div>
        </div>
      )}

      </div>{/* end content wrapper */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function VisibilityIndicator({
  isInternal,
  internalLabel,
  externalLabel,
}: {
  isInternal: boolean
  // Passed in rather than derived here: the badge must name the same audience
  // the author was offered when they chose the visibility.
  internalLabel: string
  externalLabel: string
}) {
  if (isInternal) {
    return (
      <span className="inline-flex shrink-0 items-center gap-[3px] text-[9px] font-normal tracking-[0.04em] text-[var(--ink-3)]">
        <Lock className="h-[9px] w-[9px]" />
        {internalLabel}
      </span>
    )
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-[3px] text-[9px] font-normal tracking-[0.04em] text-[var(--green)]">
      <Eye className="h-[9px] w-[9px]" />
      {externalLabel}
    </span>
  )
}

// The stage the record was in when the note was written (WPS-153): the
// record's own status label, from the API, so it reads exactly like the header.
// Shown bare, as the client drew it; the hover says what it is. "Status", not
// "Stage", so it never reads against a claim header's separate Stage chip.
function NoteStage({ stage }: { stage?: string }) {
  const { t } = useTranslation()
  if (!stage) return null
  const hint = t('notes.statusWhenWritten', 'Status when the note was written')
  return (
    <span
      title={`${hint}: ${stage}`}
      className="inline-flex min-w-0 max-w-[200px] items-center gap-1 text-[11px] font-normal text-[var(--ink-2)]"
    >
      <Milestone aria-hidden="true" className="h-[11px] w-[11px] shrink-0" />
      <span className="sr-only">{hint}: </span>
      <span className="truncate">{stage}</span>
    </span>
  )
}

function CategoryBadge({ category }: { category: NoteCategory }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-[3px] border-[0.5px] px-1.5 py-px text-[9px] font-normal uppercase tracking-[0.1em]',
        CATEGORY_STYLES[category]
      )}
    >
      {category}
    </span>
  )
}

function FilterPill({
  label,
  active,
  category,
  onClick,
}: {
  label: string
  active: boolean
  category?: NoteCategory
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={cn(
        'cursor-pointer rounded-[4px] border px-2 py-[3px] text-[9px] font-normal uppercase tracking-[0.1em] transition-all duration-150',
        active
          ? category
            ? CATEGORY_STYLES[category]
            : 'border-[var(--green)] bg-[var(--green-bg)] text-[var(--green)]'
          : 'border-[0.5px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)]'
      )}
    >
      {label}
    </Button>
  )
}
